import React, { useRef, type InputHTMLAttributes, useEffect } from "react";

import { APP_COLORS } from "@styles/colors";
import { cn } from "@utils/tailwind.util";

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  variant?: "small" | "medium";
  indeterminate?: boolean;
  isChecked?: boolean;
  customBackgroundImage?: string;
  customBackgroundColor?: string;
  customStyle?: React.CSSProperties;
  labelClassName?: string;
  parentClassName?: string;
}

export function Checkbox({
  label,
  variant = "medium",
  id,
  className = "",
  isChecked = false,
  indeterminate = false,
  customBackgroundImage,
  customBackgroundColor,
  customStyle,
  labelClassName,
  parentClassName,
  ...restInputProps
}: CheckboxProps) {
  const cRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (cRef.current) {
      cRef.current.indeterminate = indeterminate;
    }
  }, [cRef, indeterminate]);

  const defaultCheckedBackgroundImage =
    'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"%3E%3Cpath fill="none" stroke="%23' +
    APP_COLORS.gray[50].substring(1) +
    '" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.5 7.5L6.5 10.5l6-6"%3E%3C/path%3E%3C/svg%3E\')';

  const checkboxStyle = isChecked
    ? {
        backgroundSize: "70%",
        backgroundPosition: "center center",
        backgroundImage: customBackgroundImage || defaultCheckedBackgroundImage,
        backgroundColor: customBackgroundColor,
      }
    : indeterminate
      ? {
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='${encodeURIComponent(
            APP_COLORS.gray[50]
          )}' d='M4 7h8v2H4z'%3E%3C/path%3E%3C/svg%3E")`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }
      : {};

  return (
    <div className={cn("flex flex-shrink-0 items-center", parentClassName)}>
      <input
        ref={cRef}
        id={id}
        type="checkbox"
        value=""
        style={{ ...checkboxStyle, ...customStyle }}
        className={cn(
          "focus:outline-none focus:ring-0 focus:ring-offset-0 focus:outline-offset-0",
          "checkbox !border-primary-600 h-4 w-4 rounded bg-background-tertiary hover:bg-gray-200 text-gray-200 focus:ring-2 disabled:border-content-disabled",
          "checked:bg-teal-400 checked:focus:bg-teal-400 checked:focus:hover:bg-teal-500  checked:hover:bg-teal-500 checked:border-teal-500 focus:ring-0",
          "dark:bg-gray-450 dark:hover:bg-gray-400 dark:checked:bg-teal-400 dark:checked:hover:bg-teal-500",
          { "h-5 w-5 rounded-md": variant === "medium" },
          className
        )}
        checked={isChecked}
        {...restInputProps}
      />
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "ml-2 text-sm font-medium text-gray-600 dark:text-invertedPrimary80",
            labelClassName
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
}
